import { useState, useCallback } from 'react';
import { resetPassword } from '../../../services/authService';
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from '../constants';

/**
 * Hook para gerenciar alteração de senha
 *
 * @returns Estado e funções para alterar senha
 *
 * @example
 * ```tsx
 * const { changePassword, loading, error, success, clearMessages } = usePasswordChange();
 *
 * const handleSubmit = async () => {
 *   await changePassword('newPassword123!');
 * };
 * ```
 */
export const usePasswordChange = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  /**
   * Altera a senha do usuário
   *
   * @param newPassword - Nova senha
   */
  const changePassword = useCallback(async (newPassword: string) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await resetPassword(newPassword);

      setSuccess(SUCCESS_MESSAGES.passwordUpdated);
    } catch (err: unknown) {
      const errorMessage = (err as any)?.message ||
                          ERROR_MESSAGES.updatePassword;

      setError(errorMessage);
      console.error('Error changing password:', err);
      throw err; // Re-throw to allow caller to handle
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Limpa mensagens de erro e sucesso
   */
  const clearMessages = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  return {
    changePassword,
    loading,
    error,
    success,
    clearMessages
  };
};
